import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import { Box, Paper, Stack, Typography } from '@mui/material';

import { fetchPositions, posiitonsSelectors } from '@redux/positions';
import { selectPermissions } from '@redux/accounts';
import {
  candidateDetailsSelectors,
  fetchCandidateDetails,
  setCandidateDetailsAction,
} from '@redux/candidateDetails';
import {
  applyCandidateFilters,
  candidateDetailsPath,
  countInactiveCandidates,
  filterInactiveCandidates,
  isStatusComplete,
  isStatusForbidden,
  isStatusIdle,
  isStatusLoading,
  mergeDuplicatePositions,
  sortPositionsByCandidates,
} from '@utils';
import { CandidateDetailsAction, hasPermission } from '@constants';
import {
  useCandidatesViewState,
  useFilterOptions,
  useIsPhone,
  useTitle,
} from '@hooks';
import { PageTitle } from 'constants/pageTitle';
import { logEvent } from '@services';
import {
  CandidateAskQuestionModal,
  CandidateDrawer,
  CandidatesHeader,
  CandidatesList,
  CandidatesToolbar,
  ClientFeedbackModal,
  ClientRescheduleCancelModal,
  defaultCandidateColumns,
  NoCandidatesState,
  NoPositionsState,
  NotAllowed,
  PageLoader,
} from '@components';
import { NoMatchesState } from 'components/common/NoMatchesState/NoMatchesState';
import { transparentPanelSx } from 'theme/surfaces';

const NoCandidatesYetState = () => (
  <Stack
    gap="0.5rem"
    flexDirection="column"
    py="3rem"
    px="5rem"
    bgcolor="background.backgroundPrimary"
    alignItems="center"
    borderRadius="1rem"
    my="3.5rem"
    mx="auto"
  >
    <img
      src="https://ae-cp-static-files.s3.eu-central-1.amazonaws.com/client-dashboard/team.png"
      alt="team"
      width="172"
      height="172"
    />
    <Typography variant="h2" textAlign="center">
      We’re pre-screening candidates for you
    </Typography>
    <Typography variant="h5" color="text.secondary" textAlign="center">
      Please expect an email from us once we have suitable matches
    </Typography>
  </Stack>
);

interface ICandidatesPageProps {
  /** A guest sees the same page with their own heading and a line on what was shared. */
  title?: string;
  intro?: React.ReactNode;
}

export const CandidatesPage: React.FC<ICandidatesPageProps> = ({
  title,
  intro,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const isPhone = useIsPhone();
  const setPageTitle = useTitle();

  const [openCandidateId, setOpenCandidateId] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle(PageTitle.CANDIDATES_PAGE);
  }, [setPageTitle]);

  const { apiStatus, data } = useAppSelector(
    posiitonsSelectors.getPositionsData,
  );
  const defaultColumns = useMemo(
    () => defaultCandidateColumns(filterInactiveCandidates(data)),
    [data],
  );
  const positionIds = useMemo(
    () => mergeDuplicatePositions(data).map((position) => position.id),
    [data],
  );
  const { state, write, toggleSort, reset, activeFilterCount, isDefault } =
    useCandidatesViewState(defaultColumns, positionIds);
  const arePermissionsLoading = useAppSelector(
    (state) => state.accounts.loading,
  );
  const canReadHiring = hasPermission(useAppSelector(selectPermissions), 'H:R');
  const {
    provideClientFeedbackAPIStatus,
    provideClientInterviewFeedbackAPIStatus,
    rescheduleCandidateInterviewAPIStatus,
    cancelCandidateInterviewAPIStatus,
  } = useAppSelector(candidateDetailsSelectors.getCandidateDetailsData);

  const normalizedPositionsData = useMemo(() => {
    const filteredPositions = state.showRejected
      ? data
      : filterInactiveCandidates(data);
    const mergedPositions = mergeDuplicatePositions(filteredPositions);
    return sortPositionsByCandidates(mergedPositions);
  }, [data, state.showRejected]);

  const inactiveCount = useMemo(() => countInactiveCandidates(data), [data]);
  const filterOptions = useFilterOptions(normalizedPositionsData);

  const visiblePositionsData = useMemo(
    () => applyCandidateFilters(normalizedPositionsData, state),
    [normalizedPositionsData, state],
  );

  const onCandidateClick = useCallback(
    (id: string) => {
      logEvent('candidate_info_review');

      if (isPhone) {
        navigate(candidateDetailsPath(id, search));
        return;
      }

      setOpenCandidateId(id);
    },
    [isPhone, navigate, search],
  );

  const onCandidateAction = useCallback(
    async (id: string, action: CandidateDetailsAction) => {
      setOpenCandidateId(null);

      const res = await dispatch(fetchCandidateDetails(id));
      if (!fetchCandidateDetails.fulfilled.match(res)) return;

      dispatch(setCandidateDetailsAction(action));

      if (action === CandidateDetailsAction.SKIP_CANDIDATE)
        logEvent('candidate_skipped');
      if (action === CandidateDetailsAction.APPROVE_CANDIDATE)
        logEvent('candidate_approved');
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      [
        provideClientFeedbackAPIStatus,
        provideClientInterviewFeedbackAPIStatus,
        rescheduleCandidateInterviewAPIStatus,
        cancelCandidateInterviewAPIStatus,
      ].some(isStatusComplete)
    ) {
      dispatch(fetchPositions());
    }
  }, [
    dispatch,
    provideClientFeedbackAPIStatus,
    provideClientInterviewFeedbackAPIStatus,
    rescheduleCandidateInterviewAPIStatus,
    cancelCandidateInterviewAPIStatus,
  ]);

  const isLoadingPositions =
    arePermissionsLoading ||
    isStatusLoading(apiStatus) ||
    (canReadHiring && isStatusIdle(apiStatus));

  if (isLoadingPositions && !data.length) return <PageLoader />;
  if (isStatusForbidden(apiStatus)) return <NotAllowed />;

  const isFiltered = Boolean(state.query) || activeFilterCount > 0;

  const emptyState = isFiltered ? (
    <NoMatchesState
      title="No candidates match these filters"
      description="Clear a filter or reset the view to see the full list"
    />
  ) : (
    <NoCandidatesYetState />
  );

  const hasAnyCandidate = data.some(
    (position) => (position.candidates ?? []).length > 0,
  );

  const canFilter = normalizedPositionsData.length > 0 && hasAnyCandidate;

  const controls = {
    data: normalizedPositionsData,
    filterOptions,
    view: state,
    activeFilterCount,
    isDefault,
    inactiveCount,
    onChange: write,
    onReset: reset,
  };

  return (
    <Box
      sx={{
        padding: { xs: '8px', tablet: '16px 24px 16px 16px' },
        overflowY: { xs: 'visible', tablet: 'auto' },
        height: '100%',
      }}
    >
      <Paper
        sx={{
          height: '100%',
          backgroundColor: 'white',
          ...(isPhone && transparentPanelSx),
        }}
      >
        <Stack gap={1.5}>
          <CandidatesHeader
            positions={visiblePositionsData}
            controls={canFilter ? controls : undefined}
            title={title}
          />
          {intro && (
            <Typography variant="body1" color="text.secondary" px={2}>
              {intro}
            </Typography>
          )}

          {!normalizedPositionsData.length ? (
            <NoPositionsState />
          ) : canFilter ? (
            <>
              <CandidatesToolbar {...controls} />

              <CandidatesList
                data={visiblePositionsData}
                view={state}
                onSort={toggleSort}
                onCandidateClick={onCandidateClick}
                onCandidateAction={onCandidateAction}
                emptyState={emptyState}
              />
            </>
          ) : (
            <NoCandidatesState positions={normalizedPositionsData} />
          )}
        </Stack>
      </Paper>

      {!isPhone && (
        <CandidateDrawer
          candidateId={openCandidateId}
          onClose={() => setOpenCandidateId(null)}
        />
      )}

      <ClientFeedbackModal />
      <ClientRescheduleCancelModal />
      <CandidateAskQuestionModal />
    </Box>
  );
};
